/**
 * SOP Health Tab (Phase 23.3.3 — Living SOPs)
 *
 * Embedded into SOPDetailPage as a tab pane. Three sub-views:
 *   1. Section heatmap — horizontal cards, one per section, color-coded by
 *      active signal count (healthy=emerald, attention=amber, critical=red).
 *   2. Active signals list — pending proposals + active suppressions,
 *      newest first.
 *   3. Proposal history table — all proposals for this SOP, any state,
 *      most recent 50.
 *
 * Manual "Generate proposal" trigger (trainer-gated, level >= 4) sits
 * above the history table so trainers can deliberately kick the drafter
 * on a section of their choice.
 *
 * All UI labels that refer to SOP hierarchy use the DB→UI mapping from
 * .claude/rules/critical-patterns.md:
 *   - sopSections → "Jobs"
 *   - sopTasks     → "Tasks"
 *   - sopSteps     → "Steps"
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ShieldOff,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useSopHealth,
  useTriggerSopProposal,
  type SopHealthActiveSignal,
  type SopHealthSectionSummary,
} from '../../../hooks/useSopHealth';
import { useRoleLevel } from '../../../lib/hooks/useRoleLevel';

interface SopHealthTabProps {
  sopId: string;
}

export function SopHealthTab({ sopId }: SopHealthTabProps) {
  const { data, isLoading, error } = useSopHealth(sopId);
  const { isTrainerOrHigher } = useRoleLevel();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        <span className="ml-3 text-sm text-gray-500">Loading health…</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-16 text-center text-sm text-red-600">
        Failed to load SOP health data.
      </div>
    );
  }

  const {
    sectionHealth,
    activeSignals,
    proposalHistory,
  } = data;

  const overallHealthy = sectionHealth.every(
    (s) => s.healthStatus === 'healthy'
  );

  return (
    <div className="space-y-8">
      {/* ── Section heatmap ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">
            Job Health
          </h3>
          {overallHealthy && sectionHealth.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5" />
              All jobs healthy
            </span>
          )}
        </div>
        {sectionHealth.length === 0 ? (
          <EmptyHealthState />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sectionHealth.map((section) => (
              <HealthCard key={section.sectionId} section={section} />
            ))}
          </div>
        )}
      </section>

      {/* ── Manual trigger (trainer-gated) ──────────────────────── */}
      {isTrainerOrHigher && sectionHealth.length > 0 && (
        <ManualTriggerPanel
          sopId={sopId}
          sections={sectionHealth}
        />
      )}

      {/* ── Active signals ──────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
          Active Signals
          {activeSignals.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
              {activeSignals.length}
            </span>
          )}
        </h3>
        {activeSignals.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-500">
            No active signals. Nothing to review or suppressed.
          </div>
        ) : (
          <ul className="space-y-2">
            {activeSignals.map((signal, idx) => (
              <ActiveSignalRow key={`${signal.signalType}-${idx}`} signal={signal} />
            ))}
          </ul>
        )}
      </section>

      {/* ── Proposal history ────────────────────────────────────── */}
      <section>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">
          Proposal History
          {proposalHistory.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
              {proposalHistory.length}
            </span>
          )}
        </h3>
        {proposalHistory.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-500">
            No proposals have been drafted for this SOP yet.
          </div>
        ) : (
          <ProposalHistoryTable items={proposalHistory} />
        )}
      </section>
    </div>
  );
}

// ============================================
// Section heatmap cards
// ============================================

function HealthCard({ section }: { section: SopHealthSectionSummary }) {
  const palette = healthPalette(section.healthStatus);
  const Icon =
    section.healthStatus === 'healthy'
      ? CheckCircle2
      : section.healthStatus === 'attention'
        ? Activity
        : AlertTriangle;

  return (
    <div
      className={`rounded-xl border p-4 ${palette.bg} ${palette.border}`}
      title={`${section.activeSignalCount} active ${section.activeSignalCount === 1 ? 'signal' : 'signals'} (${section.pendingProposals} pending proposals, ${section.activeSuppressions} suppressions)`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-[11px] font-bold ${palette.badge}`}>
              {section.sectionNumber}
            </span>
            <Icon className={`w-4 h-4 ${palette.text}`} />
          </div>
          <p
            className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2"
            title={section.sectionTitle}
          >
            {section.sectionTitle}
          </p>
        </div>
        <div className={`flex-shrink-0 text-right ${palette.text}`}>
          <div className="text-2xl font-bold leading-none">
            {section.activeSignalCount}
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider mt-1">
            {section.activeSignalCount === 1 ? 'signal' : 'signals'}
          </div>
        </div>
      </div>
      {section.activeSignalCount > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200/60 flex items-center gap-3 text-[11px] text-gray-600">
          <span>
            <strong>{section.pendingProposals}</strong> pending
          </span>
          <span className="text-gray-300">|</span>
          <span>
            <strong>{section.activeSuppressions}</strong> suppressed
          </span>
        </div>
      )}
    </div>
  );
}

function healthPalette(status: 'healthy' | 'attention' | 'critical') {
  if (status === 'healthy') {
    return {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      badge: 'bg-emerald-100 text-emerald-700',
    };
  }
  if (status === 'attention') {
    return {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      badge: 'bg-amber-100 text-amber-700',
    };
  }
  return {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-700',
  };
}

// ============================================
// Active signal row
// ============================================

function ActiveSignalRow({ signal }: { signal: SopHealthActiveSignal }) {
  if (signal.signalType === 'pending_proposal') {
    return (
      <li className="bg-white rounded-xl border border-amber-100 p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold uppercase tracking-wide">
                Pending review
              </span>
              {signal.source && (
                <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  {signal.source.replace(/_/g, ' ')}
                </span>
              )}
              {typeof signal.impactScore === 'number' && (
                <span className="text-[11px] font-semibold text-gray-600">
                  Impact {signal.impactScore}
                </span>
              )}
              {typeof signal.confidence === 'number' && (
                <span className="text-[11px] text-gray-500">
                  Confidence {(signal.confidence * 100).toFixed(0)}%
                </span>
              )}
              <span className="text-[11px] text-gray-400 ml-auto">
                {formatRelativeTime(signal.createdAt)}
              </span>
            </div>
            {signal.rationale && (
              <p className="mt-1.5 text-sm text-gray-700 leading-snug line-clamp-2">
                {signal.rationale}
              </p>
            )}
            {signal.proposalId && (
              <Link
                to={`/sops/proposals/${signal.proposalId}`}
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#00674a] hover:text-[#005540]"
              >
                Review proposal
                <ChevronRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      </li>
    );
  }

  // Suppressed
  const permanent = signal.suppressedUntil == null;
  const until = signal.suppressedUntil
    ? new Date(signal.suppressedUntil)
    : null;
  return (
    <li className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <ShieldOff className="w-4 h-4 text-gray-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-[11px] font-semibold uppercase tracking-wide">
              Suppressed
            </span>
            {signal.source && (
              <span className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                {signal.source.replace(/_/g, ' ')}
              </span>
            )}
            {signal.suppressionReason && (
              <span className="text-[11px] font-semibold text-gray-600">
                Reason: {signal.suppressionReason}
              </span>
            )}
            <span className="text-[11px] text-gray-400 ml-auto">
              {formatRelativeTime(signal.createdAt)}
            </span>
          </div>
          <p className="mt-1.5 text-sm text-gray-600 leading-snug">
            {signal.sectionTitle ? (
              <>
                In job{' '}
                <span className="font-semibold text-gray-800">
                  {signal.sectionTitle}
                </span>
                .{' '}
              </>
            ) : null}
            {permanent
              ? 'Permanent — safety-related rejection.'
              : until
                ? `Until ${until.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`
                : null}
          </p>
        </div>
      </div>
    </li>
  );
}

// ============================================
// Manual trigger panel (trainer-gated)
// ============================================

interface ManualTriggerPanelProps {
  sopId: string;
  sections: SopHealthSectionSummary[];
}

function ManualTriggerPanel({ sopId, sections }: ManualTriggerPanelProps) {
  // Auto-pick the highest-signal section as the default. Ties break by
  // lowest sectionNumber (stable ordering). When all sections are healthy,
  // we still populate the dropdown with the first section so the trainer
  // can opt in from a known-good starting point.
  const defaultSectionId = useMemo(() => {
    if (sections.length === 0) return '';
    const ranked = [...sections].sort((a, b) => {
      const byCount = b.activeSignalCount - a.activeSignalCount;
      if (byCount !== 0) return byCount;
      return a.sectionNumber - b.sectionNumber;
    });
    return ranked[0]?.sectionId ?? '';
  }, [sections]);

  const [sectionId, setSectionId] = useState<string>(defaultSectionId);
  const [note, setNote] = useState<string>('');

  const trigger = useTriggerSopProposal();

  const handleSubmit = async () => {
    if (!sectionId) return;
    const trimmedNote = note.trim();
    try {
      await trigger.mutateAsync({
        sopId,
        sectionId,
        sourceDescription: trimmedNote.length > 0 ? trimmedNote : undefined,
      });
      toast.success('Drafter enqueued — new proposal will appear shortly.');
      setNote('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to enqueue drafter.';
      toast.error(message);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-[#00674a]/20 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#c5e8c2] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-4 h-4 text-[#00674a]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">
            Generate proposal
          </p>
          <p className="text-xs text-gray-500">
            Kick the AI drafter on a specific job. The proposal lands in the
            review inbox for four-eyes approval.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-4">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Job
          </label>
          <select
            value={sectionId}
            onChange={(e) => setSectionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none"
            disabled={trigger.isPending}
          >
            {sections.map((section) => (
              <option key={section.sectionId} value={section.sectionId}>
                {section.sectionNumber}. {section.sectionTitle}
                {section.activeSignalCount > 0
                  ? ` (${section.activeSignalCount} signal${section.activeSignalCount === 1 ? '' : 's'})`
                  : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-6">
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Note (optional, max 500 chars)
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            placeholder="Why are you triggering this draft?"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none"
            disabled={trigger.isPending}
          />
        </div>
        <div className="md:col-span-2 md:pt-[22px]">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!sectionId || trigger.isPending}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#00674a] text-white font-semibold text-sm rounded-lg hover:bg-[#005540] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {trigger.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Queuing…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

// ============================================
// Proposal history table
// ============================================

interface ProposalHistoryTableProps {
  items: Array<{
    proposalId: string;
    source: string;
    state: string;
    rationale: string;
    impactScore: number;
    confidence: number;
    reviewerUserName?: string;
    reviewedAt?: string;
    createdAt: string;
  }>;
}

function ProposalHistoryTable({ items }: ProposalHistoryTableProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3">State</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Impact</th>
              <th className="px-4 py-3">Confidence</th>
              <th className="px-4 py-3">Reviewer</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => {
              const canNavigate = item.state !== 'draft';
              const row = (
                <>
                  <td className="px-4 py-3">
                    <StateBadge state={item.state} />
                  </td>
                  <td className="px-4 py-3 text-gray-700 capitalize">
                    {item.source.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3 text-gray-700 font-semibold">
                    {item.impactScore}
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {(item.confidence * 100).toFixed(0)}%
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {item.reviewerUserName ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {formatRelativeTime(item.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canNavigate ? (
                      <ChevronRight className="w-4 h-4 text-gray-300 inline" />
                    ) : null}
                  </td>
                </>
              );
              return canNavigate ? (
                <tr
                  key={item.proposalId}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    window.location.href = `/sops/proposals/${item.proposalId}`;
                  }}
                >
                  {row}
                </tr>
              ) : (
                <tr key={item.proposalId} className="bg-white">
                  {row}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  const palette = stateBadgePalette(state);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold capitalize ${palette}`}
    >
      {state.replace(/_/g, ' ')}
    </span>
  );
}

function stateBadgePalette(state: string): string {
  switch (state) {
    case 'pending_review':
      return 'bg-amber-100 text-amber-800';
    case 'approved':
    case 'published':
      return 'bg-emerald-100 text-emerald-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'superseded':
      return 'bg-gray-100 text-gray-600';
    case 'draft':
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// ============================================
// Empty health state
// ============================================

function EmptyHealthState() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
      <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
        <Activity className="w-6 h-6 text-gray-400" />
      </div>
      <p className="text-sm font-semibold text-gray-700">
        No jobs to evaluate yet
      </p>
      <p className="text-xs text-gray-500 mt-1">
        Process this SOP to extract jobs, tasks, and steps — health data
        lands once signals start accumulating.
      </p>
    </div>
  );
}

// ============================================
// Helpers
// ============================================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
